/**
 * Client-side referral-code tracking.
 *
 * Flow:
 *   1. Visitor arrives at any URL with ?ref=CODE.
 *   2. captureFromUrl() stores the code in localStorage under sdc_ref and
 *      fires POST /api/referral/event with kind="click".
 *   3. On any conversion (intake submit, purchase confirm, etc.) callers
 *      invoke fireConversion({ caseId?, tier? }) which reads the stored
 *      code and fires kind="conversion".
 *
 * Design notes:
 *   - Silent on unknown codes (backend responds 200 unknown_code).
 *   - Idempotent: click fires once per session (per code). Conversions
 *     fire whenever caller invokes them.
 *   - No PII stored in localStorage.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "sdc_ref";
const SESSION_FLAG = "sdc_ref_click_fired";

/**
 * Read ?ref= from the current URL, persist, and fire a click event.
 * Safe to call on every page load — deduped via sessionStorage.
 */
export function captureFromUrl() {
  try {
    const url = new URL(window.location.href);
    const code = (url.searchParams.get("ref") || "").trim().toUpperCase();
    if (code) {
      localStorage.setItem(STORAGE_KEY, code);
      // Fire click once per session for this code
      const alreadyFired = sessionStorage.getItem(SESSION_FLAG + ":" + code);
      if (!alreadyFired) {
        sessionStorage.setItem(SESSION_FLAG + ":" + code, "1");
        fetch(`${API}/referral/event`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, kind: "click" }),
        }).catch(() => {});
      }
    }
  } catch (_e) {
    // Silent — tracking must never break the site.
  }
}

/**
 * Read the persisted referral code (if any). Returns null when absent.
 */
export function getStoredCode() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch (_e) {
    return null;
  }
}

/**
 * Fire a conversion event tied to the persisted referral code, if any.
 * Callers pass { caseId?, tier? } for correlation. No-op when no code.
 */
export function fireConversion({ caseId, tier } = {}) {
  const code = getStoredCode();
  if (!code) return;
  try {
    fetch(`${API}/referral/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, kind: "conversion", caseId, tier }),
    }).catch(() => {});
  } catch (_e) {
    // Silent
  }
}
